import {
  LogoMTREnterpriseFullPurple,
  LogoMTRFullPurple,
  Text,
} from '@dsny/dsny-component-library';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AuthState,
  getAvatarInfo,
  getSessionUser,
  logout,
} from 'src/features/Auth';
import RadioStationModal from 'src/features/RadioStationModal';
import theme from 'src/styles/theme';
import {
  Attachmoney,
  GroupsUnfilled,
  Sell,
  Logout,
  Keyboardarrowup,
  Keyboardarrowdown,
  Radio,
  Settings,
  Singlerecord,
} from '@dsny/dsny-component-library/icons';
import PromoPriceModal from 'src/features/PromoPriceModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/app/store';
import PricingModal from 'src/features/PricingModal';
import {
  AddPaymentObj,
  getTeamMembers,
  MemberStatus,
} from 'src/features/Purchase/AddPayment';
import { useHasMultiCompanyAccess } from 'src/features/MultiCompanyAccess';
import { formatEmailToShort } from 'src/utils/Formatter';
import { setMenuState } from 'src/features/Home';
import { useNavigate } from 'react-router-dom';
import { getPromotions, updateModalStatus } from 'src/features/Dashboard';
import { getEnterprisePlanFromLocalStorage } from 'src/utils/LocalStorageManager';
import AddNewTrackDesktop from 'src/features/Dashboard/AddNewTrack/AddNewTrackDesktop';
import { EnterprisePlanLimitModal } from 'src/features/Enterprise/PlanLimit/EnterprisePlanLimitModal';
import {
  NavContainer,
  Menu,
  MenuItem,
  PromoPriceButton,
  PricingButton,
  SignoutButtonMobile,
  AccountNameWrapper,
  AccountBadgeMobileWrapper,
  AccountInfoWrapper,
  AccountNameEmailWrapper,
  StationListButtonMobile,
  IconWrapper,
  SidebarMobile,
  LogoWrapperMobile,
  LogoWrapperDesktop,
  PreferencesButtonMobile,
  ItemDropdownMobile,
  ItemDropdownButton,
  ItemDescMobile,
  MenuItemWrapper,
} from './Navbar.styles';
import { resetIdentifyUser, trackEvent } from '../../utils/segmentService';
import AccountDropdown from './AccountDropdown/AccountDropdown';
import AccountBadges from './AccountBadges/AccountBadges';

const SHOWED_PROMO_MODAL = 'SHOWED_PROMO_MODAL';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showStationModal, setShowStationModal] = useState(false);
  const [showPromoPriceModal, setShowPromoPriceModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [isTeamSpaceVisibleMobile, setTeamSpaceVisibleMobile] = useState(false);
  const [isEnterpriseLimitVisibleMobile, setEnterpriseLimitVisibleMobile] =
    useState(false);
  const { userInfo }: AuthState = useSelector((state: RootState) => state.auth);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [isEnterprise, setIsEnterprise] = useState(undefined);
  const { modalControl } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    if (!userInfo) dispatch(getAvatarInfo());
  }, [userInfo]);

  const { promotionData } = useSelector(
    (state: RootState) => state.dashboard.promotion
  );

  const { teamMember }: AddPaymentObj = useSelector(
    (state: RootState) => state.addPayment
  );
  const hasMultiCompanyAccess = useHasMultiCompanyAccess();
  const shouldShowTeamSpace =
    teamMember?.memberStatus === MemberStatus.TEAM_MEMBER ||
    hasMultiCompanyAccess;

  // Getting session user fullname and email
  const { fullname, email } = getSessionUser();

  // Handling enterprise limit modal
  const handleShowEnterpriseLimit = (displayEnterpriseLimit: boolean) => {
    dispatch(updateModalStatus(displayEnterpriseLimit));
  };

  // Show Promo Price modal and update local storage
  useEffect(() => {
    if (promotionData) {
      const showPromoModal = JSON.parse(
        localStorage.getItem(SHOWED_PROMO_MODAL) || '[]'
      ) as string[];

      const { title } = promotionData;

      const isTitlePresent = showPromoModal.includes(title);

      if (!isTitlePresent) {
        showPromoModal.push(title);

        localStorage.setItem(
          SHOWED_PROMO_MODAL,
          JSON.stringify(showPromoModal)
        );

        setShowPromoPriceModal(true);
      }
    }
  }, [promotionData]);

  useEffect(() => {
    const { display } = getSessionUser();
    if (display && !teamMember?.memberStatus) {
      dispatch(getTeamMembers());
    }
  }, [teamMember]);

  // Handling signout
  const signOut = async () => {
    const isUserLoggedOut = await logout();
    if (isUserLoggedOut) {
      resetIdentifyUser();
      trackEvent('Logout', {}, () => {
        window.location.reload();
      });
    }
  };

  // Handling team space toggle mobile
  const teamSpaceToggleMobile = () => {
    setTeamSpaceVisibleMobile((prev) => !prev);
  };

  // Handling team space toggle mobile
  const enterpriseLimitToggleMobile = () => {
    setEnterpriseLimitVisibleMobile((prev) => !prev);
  };

  // Close the menu if the click is outside the menu component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        componentRef.current &&
        !componentRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Attach the click event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsOpen]);

  const openSideMenu = () => {
    dispatch(setMenuState(true));
  };

  // Handling navbar navigation
  const handleNavigation = () => {
    setIsOpen(false);
    navigate('/billing');
  };

  const { enterpriseProfile } = useSelector(
    (state: RootState) => state.dashboard
  );

  useEffect(() => {
    if (!promotionData) {
      dispatch(getPromotions());
    }
    setIsEnterprise(getEnterprisePlanFromLocalStorage());
  }, [enterpriseProfile]);

  if (isEnterprise === undefined) {
    return null;
  }

  return (
    <NavContainer ref={componentRef} isOpen={isOpen}>
      {/* Sidebar - mobile */}
      <SidebarMobile onClick={() => openSideMenu()}>
        <span />
        <span />
        <span />
      </SidebarMobile>

      {/* Logos - mobile */}
      <LogoWrapperMobile>
        {isEnterprise && (
          // Enterprise
          <LogoMTREnterpriseFullPurple />
        )}

        {!isEnterprise && (
          // Default
          <LogoMTRFullPurple />
        )}
      </LogoWrapperMobile>
      <LogoWrapperDesktop>
        <div />
      </LogoWrapperDesktop>

      {/* Account badge button - mobile */}
      <AccountBadgeMobileWrapper>
        <AccountBadges
          avatarColor={userInfo?.color || ''}
          avatarInitals={userInfo?.initials || ''}
          onClick={() => setIsOpen(!isOpen)}
        />
      </AccountBadgeMobileWrapper>

      <Menu isOpen={isOpen}>
        <MenuItem>
          {/* Account info - mobile */}
          <AccountInfoWrapper>
            {/* User fullname and email */}
            <AccountNameEmailWrapper>
              <AccountBadges
                avatarColor={userInfo?.color || ''}
                avatarInitals={userInfo?.initials || ''}
              />
              <AccountNameWrapper>
                <Text ellipsis maxWidth="216px" color={theme.colors.neutralW40}>
                  {fullname}
                </Text>
                <Text
                  fontSize="12px"
                  ellipsis
                  maxWidth="216px"
                  color={theme.colors.neutralW40}
                >
                  {formatEmailToShort(email)}
                </Text>
              </AccountNameWrapper>
            </AccountNameEmailWrapper>

            {/* Enterprise limit */}
            {enterpriseProfile.isEnterpriseProfile && (
              <>
                <ItemDropdownMobile onClick={enterpriseLimitToggleMobile}>
                  <ItemDropdownButton>
                    <Singlerecord />
                    {t('ENTERPRISE_PLAN_LIMIT')}{' '}
                    {`${enterpriseProfile.active_songs_count}/${enterpriseProfile.songs_limit}`}
                  </ItemDropdownButton>
                  {isEnterpriseLimitVisibleMobile ? (
                    <Keyboardarrowup />
                  ) : (
                    <Keyboardarrowdown />
                  )}
                </ItemDropdownMobile>
                <ItemDescMobile isOpen={isEnterpriseLimitVisibleMobile}>
                  <Trans
                    i18nKey="ENTERPRISE_PLAN_LIMIT_ACCOUNT_DESC"
                    values={{ limit: enterpriseProfile.songs_limit }}
                  />
                </ItemDescMobile>
              </>
            )}

            {/* Team space */}
            {shouldShowTeamSpace && (
              <>
                <ItemDropdownMobile onClick={teamSpaceToggleMobile}>
                  <ItemDropdownButton>
                    <GroupsUnfilled width={24} height={24} display="flex" />
                    {t('TEAM_SPACE_NAV_CTA')}
                  </ItemDropdownButton>
                  {isTeamSpaceVisibleMobile ? (
                    <Keyboardarrowup />
                  ) : (
                    <Keyboardarrowdown />
                  )}
                </ItemDropdownMobile>
                <ItemDescMobile isOpen={isTeamSpaceVisibleMobile}>
                  {t('TEAM_SPACE_DESC')}
                </ItemDescMobile>
              </>
            )}
          </AccountInfoWrapper>

          {/* Promo Price modal button - mobile and desktop */}
          {!enterpriseProfile.isEnterpriseProfile && !!promotionData ? (
            <div>
              <PromoPriceButton onClick={() => setShowPromoPriceModal(true)}>
                <IconWrapper>
                  <Sell fill={theme.colors.mtr60} />
                </IconWrapper>
                {promotionData.name}
              </PromoPriceButton>
              <PromoPriceModal
                promotionData={promotionData}
                showPromoPriceModal={showPromoPriceModal}
                setShowPromoPriceModal={setShowPromoPriceModal}
              />
            </div>
          ) : (
            <MenuItemWrapper>&nbsp;</MenuItemWrapper>
          )}

          {/* Pricing modal button - mobile and desktop */}
          {!enterpriseProfile.isEnterpriseProfile ? (
            <div>
              <PricingButton onClick={() => setShowPricingModal(true)}>
                <IconWrapper>
                  <Attachmoney />
                </IconWrapper>
                {t('PRICING_MODAL_NAV_CTA')}
              </PricingButton>
              <PricingModal
                showPricingModal={showPricingModal}
                setShowPricingModal={setShowPricingModal}
              />
            </div>
          ) : (
            <MenuItemWrapper>&nbsp;</MenuItemWrapper>
          )}

          {/* Station list modal button - mobile */}
          <StationListButtonMobile onClick={() => setShowStationModal(true)}>
            <Radio />
            {t('NAVBAR_STATION_LIST_MODAL')}
          </StationListButtonMobile>
          <RadioStationModal
            showStationModal={showStationModal}
            setShowStationModal={setShowStationModal}
          />

          {/* Billing preferences button - mobile */}
          <PreferencesButtonMobile onClick={() => handleNavigation()}>
            <Settings />
            {t('NAVBAR_PREFERENCES_BUTTON')}
          </PreferencesButtonMobile>

          {/* Signout button - mobile */}
          <SignoutButtonMobile onClick={signOut}>
            <Logout />
            {t('NAVBAR_SIGNOUT_CTA_BUTTON')}
          </SignoutButtonMobile>

          {/* Add new track button - desktop */}
          <AddNewTrackDesktop
            setShowEnterpriseLimit={handleShowEnterpriseLimit}
          />

          {/* Account dropdown - desktop */}
          <AccountDropdown userInfo={userInfo} />
        </MenuItem>

        {/* Enterprise plan limit modal */}
        <EnterprisePlanLimitModal
          showModal={modalControl.showEnterpriseModal}
          setShowModal={handleShowEnterpriseLimit}
        />
      </Menu>
    </NavContainer>
  );
};

export default Navbar;
